import { NgClass, NgFor, NgIf } from '@angular/common';
import { AfterViewInit, Component, Input } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { IPolicy, Policy } from '@curbnturf/entities';
import {
  DisplayValidationErrorsDirective,
  formatPolicyFormValue,
  IPolicyForm,
  policyFormBuilder,
} from '@curbnturf/form-helpers';
import { siteComplete } from '@curbnturf/helpers';
import { PlatformService } from '@curbnturf/network/src/lib/platform/platform.service';
import { IonicModule } from '@ionic/angular';

@Component({
  selector: 'curbnturf-listing-rules',
  templateUrl: './listing-rules.component.html',
  styleUrls: ['./listing-rules.component.scss'],
  standalone: true,
  imports: [NgFor, NgClass, NgIf, IonicModule, FormsModule, ReactiveFormsModule, DisplayValidationErrorsDirective],
})
export class ListingRulesComponent implements AfterViewInit {
  @Input() form: FormGroup<{
    policies: FormArray<FormGroup<IPolicyForm>>;
  }>;
  @Input() hideMobile: boolean = false;

  predefinedPolicies = [
    'No Smoking',
    'No parties or events',
    'Honor quiet hours from 10 pm to 7 am',
    'No fireworks allowed on the premises',
    'No firearms allowed on the premises',
  ];

  siteComplete = siteComplete;

  customPolicies: FormArray<FormGroup<IPolicyForm>>;

  get policies() {
    if (this.form) {
      return this.form.controls.policies;
    }

    return;
  }

  get policyValues() {
    if (this.policies) {
      return this.policies.value;
    }

    return;
  }

  get isPhysicalDevice() {
    return (
      this.platformService.isPhysicalDevice() || this.platformService.isIOSWeb() || this.platformService.isAndroidWeb()
    );
  }

  constructor(
    private fb: FormBuilder,
    private platformService: PlatformService,
  ) {
    this.customPolicies = this.fb.array<FormGroup<IPolicyForm>>([]);
  }

  ngAfterViewInit() {
    this.checkIfPolicyNeeded();
    if (this.policies) {
      this.policies.valueChanges.subscribe(() => {
        this.checkIfPolicyNeeded();
      });
    }
  }

  checked(description: string): Policy | undefined {
    if (this.policyValues) {
      const foundPolicy = this.policyValues.find((policy) => policy?.description === description);

      if (foundPolicy) {
        return new Policy({
          id: foundPolicy.id || undefined,
          custom: foundPolicy.custom || false,
          description: foundPolicy.description || '',
        });
      }
    }

    return;
  }

  toggle(description: string) {
    const foundPolicy = this.checked(description);
    if (foundPolicy) {
      this.removePolicy(foundPolicy);
    } else {
      this.addPolicy({ description, custom: false });
    }

    if (this.policies) {
      this.policies.markAsDirty();
    }
  }

  descriptionChanged(policy: FormGroup<IPolicyForm>) {
    if (!policy.value.description && this.lastCustom() !== policy) {
      this.removePolicy(formatPolicyFormValue(policy));
    }

    this.checkIfPolicyNeeded();
    if (this.policies) {
      this.policies.markAsDirty();
    }
  }

  private lastCustom(): FormGroup<IPolicyForm> {
    return this.customPolicies.at(this.customPolicies.length - 1) as FormGroup<IPolicyForm>;
  }

  private checkIfPolicyNeeded() {
    this.updateCustomPolicies();

    if (this.customPolicies) {
      if (this.customPolicies.length === 0) {
        return this.addPolicy();
      }

      const descriptionInput = this.lastCustom().get('description');
      if (descriptionInput && descriptionInput.value) {
        return this.addPolicy();
      }
    }
  }

  private updateCustomPolicies() {
    if (this.policies) {
      const customFiltered = this.policies.controls.filter(
        (policy) => !this.predefinedPolicies.includes(policy.value?.description || ''),
      );

      // Make sure all custom policies have been added to the this.customPolicies array.
      customFiltered.forEach((policyForm) => {
        const policy = policyForm.value;
        if (policy.custom === true) {
          if (!this.customPolicies.controls.includes(policyForm)) {
            this.customPolicies.push(policyForm);
          }
        }
      });

      // Make sure any custom policies that no longer exist in the policies array are removed from this.customPolicies.
      const removeIndex: number[] = [];
      this.customPolicies.controls.forEach((policy, index) => {
        if (!customFiltered.find((filteredPolicy) => filteredPolicy === policy)) {
          removeIndex.push(index);
        }
      });

      if (removeIndex.length > 0) {
        removeIndex.reverse().forEach((index) => this.customPolicies.removeAt(index));
      }
    }

    return this.customPolicies;
  }

  private addPolicy(policy?: IPolicy) {
    if (this.policies) {
      this.policies.push(policyFormBuilder(this.fb, policy || { custom: true }));

      this.updateCustomPolicies();
    }
  }

  private removePolicy(policy?: IPolicy) {
    if (policy && this.policies) {
      this.policies.removeAt(
        this.policies.controls.findIndex((policyForm) => policyForm.value.description === policy.description),
      );

      this.updateCustomPolicies();
    }
  }
}
